import type { RelatedDisplayItem } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import type { AnyItem } from '#layers/data-model/public';
import type { UseM2MStateReturn } from './use-m2m-state';
import { shallowRef, watch } from 'vue';
import { isObjectEmpty } from '#layers/base/public';
import { stringifyStableId } from '#layers/data-model/public';
import { createdItemKey, injectFormBlockContext, isCreatedItem } from '#layers/forms/public';

type M2MDisplayInputs = Pick<
	UseM2MStateReturn,
	| 'items'
	| 'getTargetFromJunction'
	| 'getJunctionKey'
	| 'junctionCollection'
	| 'getItemState'
	| 'isNewTargetCreate'
	| 'isLoading'
>;

/**
 * Maps the junction rows from {@link useM2MState} into `RelatedItemListCard` display items with a
 * derived {@link RelatedItemState}, refreshing when the underlying items or load state settle.
 * Shared by every M2M list input (generic list, files, users) so the mapping lives in one place.
 */
export function useM2MDisplayItems<T extends AnyItem = AnyItem>(m2mState: M2MDisplayInputs) {
	const {
		items,
		getTargetFromJunction,
		getJunctionKey,
		junctionCollection,
		getItemState,
		isNewTargetCreate,
		isLoading,
	} = m2mState;

	const formBlockContext = injectFormBlockContext(null);

	const displayItems = shallowRef<RelatedDisplayItem<T>[]>([]);

	function getJunctionKeyString(junctionItem: AnyItem): string {
		const key = getJunctionKey(junctionItem);
		if (key && !isObjectEmpty(key) && junctionCollection.value) return stringifyStableId(key);
		if (isCreatedItem(junctionItem)) return junctionItem[createdItemKey];
		return '';
	}

	function updateDisplayItems() {
		displayItems.value = items.value.map((junctionItem) => ({
			id: getJunctionKeyString(junctionItem),
			// The caller asserts the target row type (e.g. UserAvatarUser); the junction only knows AnyItem.
			data: (getTargetFromJunction(junctionItem) ?? {}) as T,
			state: getItemState(junctionItem),
			persisted: !isNewTargetCreate(junctionItem),
		}));
	}

	watch(items, () => {
		const isFormLoading = formBlockContext?.state.value?.$loading ?? false;

		if (!isFormLoading && !isLoading.value) {
			updateDisplayItems();
		}
	}, {
		deep: true,
		immediate: true,
	});

	watch(
		[() => isLoading.value, () => formBlockContext?.state.value?.$loading],
		([newLoading, newFormLoading], [oldLoading, oldFormLoading]) => {
			if (newLoading) return;

			if ((oldLoading && !newLoading) || (oldFormLoading && !newFormLoading)) {
				updateDisplayItems();
			}
		},
	);

	return { displayItems };
}
